<template>
  <div ref="listRef" class="template-wrapper" @scroll="onScroll">
    <div class="x-container pt-[20px] pb-[48px] relative z-[20]">
      <BannerSearch
        v-model:keyword="keyword"
        :title="pageTitle"
        :area="SEARCH_RECORD_UNIQUE.TEMPLATE"
        :showRecommend="true"
        :cover="pageBackground"
        :showSearch="true"
        :placeholder="$t('qing-shu-ru-ni-gan-xing-qu-de-zuo-pin-ru-an-quan-jiao-yu')"
        @search="val => handleOnSearch(val, true)"
      />
    </div>
    <div class="w-full">
      <div ref="filterBoxRef" class="sticky header top-0 z-[11] bg-back-bg filter-box">
        <div class="x-container relative">
          <TagFilter
            :modelValue="searchForm"
            :sticky="tagsIsSmall"
            :parentHovered="isFilterBoxHovered"
            :tags="data || []"
            @update:modelValue="onModelValueChange"
            @change="handleOnChange"
          />
          <div class="w-full h-[1px] bg-back-3"></div>
          <div class="w-full flex-y-center py-5">
            <DictFilter
              v-model="searchForm.sort"
              name="sorts"
              :has-all="false"
              class="text-14 gap-x-[16px]"
              @change="handleOnChange"
            />
            <div class="justify-end flex-1 flex-y-center">
              <TemplatesScreenSelect
                v-model:resolution="searchForm.format"
                @change="onChangeImmediatelyWithClear"
              />
              <a-divider
                v-if="isChinese()"
                type="vertical"
                style="margin: 0 20px; height: 12px; background-color: var(--text-1-20)"
              ></a-divider>
              <a-select
                v-if="isChinese()"
                v-model:value="searchForm[`tag_lan`]"
                size="small"
                :dropdown-match-select-width="false"
                class="exposed-select"
                popup-class-name="expose-options"
                allow-clear
                :options="langOptions"
                :placeholder="langTag?.tag_name"
                @change="onChangeImmediatelyWithClear"
              >
                <template #suffixIcon>
                  <iconpark-icon name="arrowdown" class="text-font-60"></iconpark-icon>
                </template>
              </a-select>
            </div>
          </div>
        </div>
      </div>
      <div class="x-container">
        <a-spin :spinning="loading" class="pb-[15px]">
          <TemplateMasonry
            v-if="calList.length"
            :list="calList"
            :loading="searchStatus === 'pending'"
            class="gap-y-[0px]"
            mode="free"
            :template_source="TEMPLATE_SOURCE.TEMPLATE_PAGE"
          />
          <Empty
            v-else-if="searchStatus === 'success' && !list.length"
            :list="popularList?.data || []"
            @refresh="popularRefresh"
            @select="handleOnSearch"
          />
          <Divider v-if="list.length === total && total >= 15" />
          <div
            v-if="calList.length < total"
            ref="wp"
            v-intersect="handleInView"
            class="way-point flex-y-center items-center justify-center h-[80px]"
          />
        </a-spin>
      </div>
    </div>
  </div>
  <BackTop scrollContainer=".template-wrapper" item=".card-item" :total="total" />
</template>

<script lang="ts" setup>
import BannerSearch from '@/components/banner/search.vue';
import Empty from '@/features/searchInput/empty.vue';
import BackTop from '@rendora/common/features/back-top/index.vue';
import { compact, debounce, get, omit } from 'lodash-es';
import { getPromotionCenterPageCard, getTemplate, getTemplateSearch } from '~/api';
import type { TemplateParams } from '~/api/interface';
import { PAGE_CARD_TYPE } from '~/api/interface/publicize';
import { EditIndexType, getRandomNames, MaterialType } from '~/api/module/material';
import { getTemplateTags } from '~/api/module/tags';
import TemplateMasonry from '~/components/templates/index.vue';
import { SEARCH_RECORD_UNIQUE, TEMPLATE_SORT } from '~/constants/index';
import { useTemplateDetail } from '~/hooks/useModal/modals/template';
import { useMountedWatch } from '~/hooks/useMountedWatch';
import { SCROLL_LIST, useScrollList } from '~/hooks/useScrollList';
import { useStarPatchList } from '~/hooks/useStarPatch';
import { isChinese } from '~/utils/area';
import { TEMPLATE_SOURCE } from '~/utils/track';
import TagFilter from './components/tag-filter.vue';
const route = useRoute();
const router = useRouter();

const { $t } = useNuxtApp();
const searchForm = reactive<any>({
  sort: TEMPLATE_SORT.ALL,
  format: FORMAT.horizon,
  map_types: route.query.map_types || undefined,
  tag_lan: undefined,
});

const centerTags = useCenterTags();

const langTag = computed(() => centerTags.value.tags.find(tag => tag.outer_id === 'language'));

const langOptions = computed(() => {
  return langTag.value?.children?.map(child => ({
    label: child.tag_name,
    value: child.ids,
  }));
});

function setTagFromQuery() {
  const { tag_ids } = route.query;
  if (String(tag_ids)?.length > 0) {
    const links = String(tag_ids).split('_');

    Object.keys(searchForm).forEach(key => {
      if (key.startsWith('tag_')) {
        searchForm[key] = undefined;
      }
    });

    const tagMap = useTagMap();

    searchForm.tag_lan = undefined;

    links.forEach(l => {
      const tagItem = tagMap.value[l];

      const matchLang = langTag.value?.children?.some(item => {
        if (l.includes(item.ids)) {
          searchForm.tag_lan = item.ids;
          return true;
        }
        return false;
      });

      if (matchLang) {
        return;
      }

      const parts = l.split(':');
      const [_, rootType] = parts[0].split(',');
      if (!parts[0]) return;

      if (!searchForm[`tag_${rootType}`]) {
        searchForm[`tag_${rootType}`] = [];
      }

      if (parts[1]) {
        if (tagItem) {
          if (tagItem.children && tagItem.children.length) {
            searchForm[`tag_${rootType}`].push(...tagItem.children);
          } else {
            searchForm[`tag_${rootType}`].push(tagItem);
          }
        }
      }
    });
  }
}

const lastChangedTag = ref<string>('');

function getTagIds() {
  const tagIds: string[] = [];

  Object.keys(searchForm).forEach(key => {
    if (key.startsWith('tag_') && searchForm[key]?.[0]?.ids) {
      tagIds.push(searchForm[key]?.[0]?.ids);
    }
  });

  const langIds = langOptions.value?.map(v => v.value) || [];

  for (const langId of langIds) {
    if (tagIds.includes(langId)) {
      tagIds.splice(tagIds.indexOf(langId), 1);
    }
  }

  const filteredTagIds = tagIds.filter(v => v !== lastChangedTag.value);

  return compact([searchForm.tag_lan, ...filteredTagIds, lastChangedTag.value]).join('_');
}

watch(
  () => route.query.tag_ids,
  async () => {
    setTagFromQuery();
  },
  { immediate: true },
);

const { refresh: refreshTag, data } = useAsyncData(
  'tagDict',
  async () => {
    const tag_ids = getTagIds();
    const res = await getTemplateTags({
      tag_ids,
      group: 'template',
      resolution: searchForm.format,
    });
    const data = res.data?.tags || [];
    setTags(data, '');
    return data;
  },
  {
    watch: [() => route.query.tag_ids],
  },
);

function onModelValueChange(changedProps: any) {
  Object.keys(changedProps).forEach(key => {
    searchForm[key] = changedProps[key];
  });

  const ids = getTagIds();

  if (ids.length) {
    const query = {
      ...route.query,
      tag_ids: ids,
    };
    router.push({
      query,
    });
  } else {
    router.push({
      query: omit(route.query, ['tag_ids']),
    });
  }
}

const filterBoxRef = ref();
const listRef = ref();

const tagsIsSmall = ref(false);
const shareVisible = useShare();
const isFilterBoxHovered = useElementHover(filterBoxRef);

const keyword = ref<string>((route.query.keyword as unknown as string) || '');

const { data: popularList, refresh: popularRefresh } = useAsyncData('popularList', () =>
  getRandomNames({
    group_id: MaterialType.Template,
    size: 6,
    edit_index_type: EditIndexType.Change,
  }),
);

provideResourceLocation({
  source: SOURCE.TEMPLATE_CENTER,
});

let paramsResolution = true;

const {
  list,
  status: searchStatus,
  refresh,
  next,
  total,
  current,
  clear,
  abort,
} = useScrollList(
  async (params, controller) => {
    const tag_ids = getTagIds();

    const p: TemplateParams = {
      ...params,
      tag_ids,
      o: searchForm.sort,
      is_template_center_template: 1,
      map_types: searchForm.map_types,
      resolution: paramsResolution ? searchForm.format : undefined,
      platform: 0,
      online_status: 0,
      query_star: import.meta.client,
      q: keyword.value,
    };

    const res = await getTemplateSearch(p, controller);

    res.data?.results?.forEach(item => {
      item.query_star = import.meta.client; // 标记这些数据是否查询过star
    });

    if (!paramsResolution) {
      // 这里有个副作用
      const format = res.data?.resolution || searchForm.format;
      searchForm.format = format;
    }
    return res;
  },
  {
    key: SCROLL_LIST.TEMPLATE_LIST,
    pageSize: 40,
  },
);

const { calList, status } = useStarPatchList(list, 2);

let first = true;

const loading = ref(false);

const handleOnSearch = async (val: string, noResolution?: boolean) => {
  keyword.value = val;
  loading.value = true;
  abort();
  if (noResolution) {
    paramsResolution = false;
  }
  await refresh();
  paramsResolution = true;
  loading.value = false;
};

useMountedWatch([calList, status], async () => {
  if (first && route.query.id && status.value === 'success') {
    first = false;
    const index = list.value.findIndex(record => record.id === Number(route.query.id)) || -1;
    if (index > -1) {
      setTimeout(() => {
        listRef.value.querySelector(`.card-item:nth-child(${index + 1})`)?.click();
      }, 100);
    } else {
      const details = await getTemplate({
        template_id: Number(route.query.id),
      });
      useTemplateDetail(
        [details.data!],
        details.data!,
        {
          templateSource: TEMPLATE_SOURCE.TEMPLATE_PAGE,
          source: SOURCE.TEMPLATE_CENTER,
        },
        undefined,
      );
    }
  }
});

onBeforeUnmount(() => {
  abort();
  clear();
});

useBackToTop(() => ({
  scrollContainer: '.template-wrapper',
  item: '.card-item',
  total: total.value,
}));

const { data: pageData } = useAsyncData(async () => {
  const res = await getPromotionCenterPageCard({
    type: PAGE_CARD_TYPE.TEMPLATE,
  });
  return res.data;
});

const pageTitle = computed(() => pageData.value?.[0]?.title || '');
const pageBackground = computed(() => pageData.value?.[0]?.cover_url || '');

/**
 * 隐藏/显示元素影响 scrollHeight，在数据少的情况下，会对 200/188 的判断造成死循环
 * 用变量记录，两次 scrollHeight 不一致时停止处理
 * */
let LastScrollHeight = 0;
const onScroll = (e: any) => {
  const { scrollTop, scrollHeight, clientHeight } = e.target as HTMLBRElement;
  if (LastScrollHeight && LastScrollHeight !== scrollHeight) {
    LastScrollHeight = scrollHeight;
    return;
  }
  LastScrollHeight = scrollHeight;
  if (scrollTop > 200 && !tagsIsSmall.value) {
    tagsIsSmall.value = true;
  } else if (scrollTop <= 188 && tagsIsSmall.value) {
    tagsIsSmall.value = false;
  }

  if (!shareVisible.value) {
    return;
  }

  shareVisible.value = false;
};

const handleInView = (inView: boolean) => {
  if (inView) {
    next();
  }
};

watch(
  () => searchForm.format,
  val => {
    trackEvent(TRACK_EVENT.CLICK_TEMPLATE_FORMAT_FILTER, {
      filter: val,
      source: SOURCE.TEMPLATE_CENTER,
    });
  },
);

watch(
  () => searchForm.map_types,
  val => {
    trackEvent(TRACK_EVENT.CLICK_TEMPLATE_CENTER_STUDIO_FILTER, {
      map_types: val,
      source: SOURCE.TEMPLATE_CENTER,
    });
  },
);

watch(
  () => searchForm.sort,
  val => {
    trackEvent(TRACK_EVENT.CLICK_TEMPLATE_CENTER_SORT_FILTER, {
      sort: val,
      source: SOURCE.TEMPLATE_CENTER,
    });
  },
);

const onChangeImmediately = () => {
  if (listRef.value) {
    listRef.value.scrollTop = 0;
  }

  abort();
  refresh();
};

const onChangeImmediatelyWithClear = (v: string) => {
  if (!v) {
    searchForm.tag_lan = undefined;
  }
  onChangeImmediately();
};

const onChange = debounce(onChangeImmediately, 500);

const handleOnChange = (v: any) => {
  if (v.level > 0) {
    lastChangedTag.value = get(v, '[0].ids', '');
  }
  onChange();
};
</script>

<style scoped lang="scss">
.sorts {
  @apply font-semibold;
}

.dict-name {
  @apply text-14 flex-y-center;

  &:after {
    @apply ml-[10px] mr-[20px] w-[1px] h-[12px] block;
    content: '';
  }

  &.line:after {
    @apply bg-back-6;
  }
}

.tag-list {
  &.small {
    @apply flex-row;
    :deep(.dict-item),
    :deep(.dict-item__single),
    :deep(.tags-more) {
      display: none;
      &.active {
        display: flex;
      }
    }
  }
}
.filter-box {
  &:hover {
    .tag-list.small {
      @apply flex-col;
      :deep(.dict-item),
      :deep(.dict-item__single),
      :deep(.tags-more) {
        display: flex;
      }
    }
  }
}

.template-wrapper {
  @apply flex flex-col overflow-y-scroll relative w-full;
  height: calc(100vh - 56px);

  .header {
    background-color: var(--back-bg);
  }
}

.dict-select {
  :deep(.ant-select-selector) {
    height: 28px !important;
    border-radius: 6px;
    .ant-select-selection-placeholder {
      line-height: 25px;
      color: var(--text-1-40);
    }
    .ant-select-selection-item {
      line-height: 25px;
    }
  }
}
</style>
